import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../config";

/**
 * Full-screen public display screen for the theater entrance.
 * Subscribes to SSE `/api/display/stream` and reacts to check-ins.
 * Theme is decided by `display_theme` in the show config (overridable by ?theme=).
 */

const THEMES = ["minimalist", "festive", "cinematic", "ticker", "neon", "typewriter", "color", "custom"];

// Resolve image URL: use proxy for Drive/Box links that need it
const resolveImageUrl = (url) => {
  if (!url) return url;
  // Convert Drive share links to direct view URL before proxy
  const driveFileMatch = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (driveFileMatch) {
    const direct = `https://drive.google.com/uc?export=view&id=${driveFileMatch[1]}`;
    return `${API_URL}/api/image-proxy?url=${encodeURIComponent(direct)}`;
  }
  if (url.includes("drive.google.com")) return `${API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
  if (url.includes("app.box.com")) return `${API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
  return url;
};

const VARIANT_STYLES = {
  vip: {
    bg: "linear-gradient(135deg, #b45309, #f59e0b)",
    color: "#000",
    label: "⭐ Convidado Especial",
    overrideTheme: true,
  },
  birthday: {
    bg: "linear-gradient(135deg, #831843, #ec4899)",
    color: "#fff",
    label: "🎂 Feliz Aniversário!",
    overrideTheme: true,
  },
  first_time: {
    bg: "linear-gradient(135deg, #064e3b, #10b981)",
    color: "#fff",
    label: "✨ Primeira visita",
    overrideTheme: true,
  },
  group: {
    bg: "linear-gradient(135deg, #1e3a8a, #6366f1)",
    color: "#fff",
    label: "",
    overrideTheme: true,
  },
  default: { overrideTheme: false },
};

function applyTemplate(tpl, event) {
  if (!tpl) return event.first_name || "";
  return (tpl || "")
    .replace(/\{primeiro_nome\}/g, event.first_name || "")
    .replace(/\{nome\}/g, event.full_name || "")
    .replace(/\{ultimo_nome\}/g, event.last_name || "");
}

function useDing(enabled) {
  const ctxRef = useRef(null);
  return useCallback(() => {
    if (!enabled) return;
    try {
      if (!ctxRef.current) ctxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = ctxRef.current;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.22, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.6);
      osc.connect(gain); gain.connect(ctx.destination);
      osc.start(); osc.stop(ctx.currentTime + 0.6);
    } catch (_) {}
  }, [enabled]);
}

export default function DisplayScreen() {
  const [params] = useSearchParams();
  const [config, setConfig] = useState(null);
  const [current, setCurrent] = useState(null);
  const [recent, setRecent] = useState([]);
  const queueRef = useRef([]);
  const busyRef = useRef(false);
  const timerRef = useRef(null);
  const [idleNow, setIdleNow] = useState(Date.now());

  // Load config once
  useEffect(() => {
    const token = params.get("token") || "";
    const url = token ? `${API_URL}/api/display/config?token=${encodeURIComponent(token)}` : `${API_URL}/api/display/config`;
    axios.get(url)
      .then(r => setConfig(r.data))
      .catch(e => {
        if (e?.response?.status === 401) setConfig({ __error: "token" });
      });
  }, [params]);

  // Idle clock tick (for countdown)
  useEffect(() => {
    const t = setInterval(() => setIdleNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const ding = useDing(config?.display_sound_enabled);

  const themeOverride = params.get("theme");
  const theme = THEMES.includes(themeOverride) ? themeOverride : (config?.display_theme || "minimalist");
  const duration = (config?.display_duration || 8) * 1000;

  // Queue + animation cadence
  const processQueue = useCallback(() => {
    if (busyRef.current) return;
    const next = queueRef.current.shift();
    if (!next) return;
    busyRef.current = true;
    setCurrent(next);
    setRecent(r => [next, ...r].slice(0, 6));
    ding();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setCurrent(null);
      busyRef.current = false;
      processQueue();
    }, duration);
  }, [duration, ding]);

  // SSE connection
  useEffect(() => {
    if (!config || config.__error) return;
    const token = params.get("token") || "";
    const streamUrl = token ? `${API_URL}/api/display/stream?token=${encodeURIComponent(token)}` : `${API_URL}/api/display/stream`;
    const es = new EventSource(streamUrl);
    es.addEventListener("checkin", (e) => {
      try {
        const data = JSON.parse(e.data);
        // Respect variant toggles
        const toggles = {
          vip: config.display_variant_vip_enabled,
          birthday: config.display_variant_birthday_enabled,
          first_time: config.display_variant_first_time_enabled,
          group: config.display_variant_group_enabled,
        };
        if (data.variant !== "default" && toggles[data.variant] === false) {
          data.variant = "default";
        }
        queueRef.current.push(data);
        processQueue();
      } catch (_) {}
    });
    es.onerror = () => { /* will auto-reconnect */ };
    return () => es.close();
  }, [config, processQueue]);

  if (!config) {
    return <div style={{ background: "#000", color: "#fff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>A carregar…</div>;
  }

  if (config.__error === "token") {
    return (
      <div data-testid="display-token-error" style={{ background: "#000", color: "#fff", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", textAlign: "center" }}>
        <div>
          <h1 style={{ fontSize: "2rem", fontWeight: 800, marginBottom: "0.5rem" }}>Acesso negado</h1>
          <p style={{ color: "#a1a1aa" }}>Token inválido ou em falta. Peça ao administrador o link correcto.</p>
        </div>
      </div>
    );
  }

  // Determine what to render
  const isIdle = !current;
  const displayText = current ? applyTemplate(config.display_template, current) : "";
  const variant = current?.variant || "default";

  return (
    <div data-testid="display-screen" style={{ width: "100vw", height: "100vh", overflow: "hidden", position: "relative", background: "#000", color: "#fff" }}>
      {isIdle ? (
        <IdleScreen config={config} now={idleNow} recent={recent} theme={theme} />
      ) : (
        <CheckinScreen event={current} text={displayText} theme={theme} variant={variant} config={config} />
      )}
    </div>
  );
}

/* =================== IDLE (no check-ins right now) =================== */
function IdleScreen({ config, now, recent, theme }) {
  const ac = config.accent_color || "#f59e0b";
  const useCustom = theme === "custom" && config.display_custom_image_url;
  const idleImg = resolveImageUrl(config.display_idle_image_url || config.image_url);
  const customImg = resolveImageUrl(config.display_custom_image_url);
  // Find the nearest upcoming session (parse date loosely)
  let countdown = null;
  if (config.display_idle_show_countdown && config.sessions?.length) {
    const first = config.sessions[0];
    countdown = `${first.date} · ${first.time}`;
  }
  return (
    <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", background: useCustom ? "#000" : "linear-gradient(135deg, #09090b, #18181b)", position: "relative", overflow: "hidden" }}>
      {useCustom && (
        <>
          <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${customImg})`, backgroundSize: "cover", backgroundPosition: "center", opacity: 0.55 }} />
          <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.4), rgba(0,0,0,0.75))" }} />
        </>
      )}
      <div style={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
        {!useCustom && idleImg && (
          <img src={idleImg} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ maxHeight: "36vh", marginBottom: "5vh", opacity: 0.85, borderRadius: 16 }} />
        )}
        <div style={{ fontSize: "4vh", fontWeight: 800, letterSpacing: "-0.5px", color: "#fff", marginBottom: "1vh", textShadow: useCustom ? "0 2px 12px rgba(0,0,0,0.7)" : "none" }}>
          {config.show_name}
        </div>
        <div style={{ fontSize: "2.2vh", color: "#e4e4e7", marginBottom: "3vh", textShadow: useCustom ? "0 2px 12px rgba(0,0,0,0.7)" : "none" }}>
          {config.display_idle_message}
        </div>
        {countdown && (
          <div style={{ fontSize: "2vh", color: ac, letterSpacing: 2, textTransform: "uppercase" }}>
            Próxima sessão: {countdown}
          </div>
        )}
      </div>
      {recent.length > 0 && (
        <div style={{ position: "absolute", bottom: 24, left: 24, right: 24, display: "flex", gap: 12, color: "#a1a1aa", fontSize: "1.6vh", fontFamily: "monospace", zIndex: 2 }}>
          {recent.map((r, i) => (
            <span key={i}>✓ {r.first_name} {r.last_name}</span>
          ))}
        </div>
      )}
    </div>
  );
}

/* =================== CHECKIN (one guest at a time) =================== */
function CheckinScreen({ event, text, theme, variant, config }) {
  const v = VARIANT_STYLES[variant] || VARIANT_STYLES.default;
  const ac = config.accent_color || "#f59e0b";
  const sessionLine = [event.session_date, event.session_time].filter(Boolean).join(" · ");

  // Variant overrides background entirely
  if (v.overrideTheme) {
    return (
      <div style={{ width: "100%", height: "100%", background: v.bg, color: v.color, display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "0 6vh", animation: "fadeZoomIn 0.5s ease-out" }}>
        <Fade>
          <div style={{ fontSize: "2.8vh", letterSpacing: 4, marginBottom: "2vh", opacity: 0.9 }}>{v.label}</div>
          {variant === "group" && event.group?.name ? (
            <>
              <div style={{ fontSize: "9vh", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1 }}>{event.group.name}</div>
              <div style={{ fontSize: "3vh", marginTop: "3vh", opacity: 0.85 }}>{event.group_count ? `${event.group_count} pessoas já chegaram` : ""}</div>
            </>
          ) : (
            <>
              <div style={{ fontSize: "9vh", fontWeight: 900, letterSpacing: "-2px", lineHeight: 1 }}>{event.full_name}</div>
              {sessionLine && <div style={{ fontSize: "2.4vh", marginTop: "3vh", opacity: 0.85, letterSpacing: 2, textTransform: "uppercase" }}>{sessionLine}</div>}
            </>
          )}
          {variant === "birthday" && <Confetti />}
        </Fade>
      </div>
    );
  }

  // Regular theme
  return <ThemeRenderer theme={theme} event={event} text={text} sessionLine={sessionLine} ac={ac} config={config} />;
}

/* =================== Theme renderer =================== */
function ThemeRenderer({ theme, event, text, sessionLine, ac, config }) {
  switch (theme) {
    case "color": {
      const baseColor = config.display_custom_color || "#1af50a";
      return (
        <div style={{ width: "100%", height: "100%", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden", background: `linear-gradient(135deg, ${baseColor} 0%, #0a0a0a 100%)` }}>
          <div style={{ position: "absolute", width: "60vw", height: "60vw", background: `radial-gradient(circle, ${baseColor}55, transparent 70%)`, top: "-20vw", left: "-20vw" }} />
          <Fade>
            <div style={{ textAlign: "center", padding: "0 8vh" }}>
              <div style={{ fontSize: "2.5vh", letterSpacing: 8, color: "#fff", textTransform: "uppercase", marginBottom: "3vh", fontWeight: 500, opacity: 0.9, textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>Bem-vindo(a)</div>
              <div style={{ fontSize: "12vh", fontWeight: 800, letterSpacing: "-2px", lineHeight: 1, textShadow: "0 4px 24px rgba(0,0,0,0.6)" }}>{text || event.full_name}</div>
              {sessionLine && <div style={{ fontSize: "2.4vh", color: "#f5f5f4", marginTop: "3vh", letterSpacing: 4, textTransform: "uppercase", textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>{sessionLine}</div>}
            </div>
          </Fade>
        </div>
      );
    }
    case "custom": {
      const imgUrl = resolveImageUrl(config.display_custom_image_url);
      const darken = config.display_custom_image_darken !== false;
      return (
        <div style={{ width: "100%", height: "100%", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
          {imgUrl ? (
            <div style={{ position: "absolute", inset: 0, backgroundImage: `url(${imgUrl})`, backgroundSize: "cover", backgroundPosition: "center" }} />
          ) : (
            <div style={{ position: "absolute", inset: 0, background: "#000" }} />
          )}
          {darken && <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(0,0,0,0.35), rgba(0,0,0,0.7))" }} />}
          <Fade>
            <div style={{ textAlign: "center", position: "relative", zIndex: 1, padding: "0 8vh" }}>
              <div style={{ fontSize: "2.5vh", letterSpacing: 8, color: ac, textTransform: "uppercase", marginBottom: "3vh", fontWeight: 500, textShadow: "0 2px 12px rgba(0,0,0,0.6)" }}>Bem-vindo(a)</div>
              <div style={{ fontSize: "12vh", fontWeight: 800, letterSpacing: "-2px", lineHeight: 1, textShadow: "0 4px 24px rgba(0,0,0,0.7)" }}>{text || event.full_name}</div>
              {sessionLine && <div style={{ fontSize: "2.4vh", color: "#f5f5f4", marginTop: "3vh", letterSpacing: 4, textTransform: "uppercase", textShadow: "0 2px 12px rgba(0,0,0,0.7)" }}>{sessionLine}</div>}
            </div>
          </Fade>
        </div>
      );
    }
    case "festive":
      return (
        <div style={{ width: "100%", height: "100%", background: "linear-gradient(135deg,#7c2d12,#ea580c,#f59e0b)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", position: "relative" }}>
          <Confetti />
          <Fade>
            <div style={{ fontSize: "9vh", marginBottom: "1vh" }}>🎉</div>
            <div style={{ fontSize: "3vh", letterSpacing: 2, marginBottom: "2vh", opacity: 0.95 }}>Já chegou!</div>
            <div style={{ fontSize: "10vh", fontWeight: 900, letterSpacing: "-2px", textShadow: "0 4px 24px rgba(0,0,0,0.35)" }}>{text || event.full_name}</div>
            {sessionLine && <div style={{ marginTop: "3vh", display: "inline-block", padding: "1vh 3vh", background: "rgba(0,0,0,0.25)", borderRadius: 999, fontSize: "2.4vh" }}>{sessionLine}</div>}
          </Fade>
        </div>
      );
    case "cinematic":
      return (
        <div style={{ width: "100%", height: "100%", background: "#0a0a0a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", fontFamily: "Georgia, serif" }}>
          <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "12vh", background: "#000" }} />
          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: "12vh", background: "#000" }} />
          <Fade>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2vh", letterSpacing: 12, color: "#d4af37", textTransform: "uppercase", fontStyle: "italic", marginBottom: "3vh" }}>apresenta</div>
              <div style={{ width: 80, height: 1, background: "#d4af37", margin: "3vh auto" }} />
              <div style={{ fontSize: "10vh", fontWeight: 400, letterSpacing: 8, textTransform: "uppercase", color: "#f5f5f4" }}>{text || event.full_name}</div>
              <div style={{ width: 80, height: 1, background: "#d4af37", margin: "3vh auto" }} />
              {sessionLine && <div style={{ fontSize: "2vh", color: "#a8a29e", letterSpacing: 8, textTransform: "uppercase", fontStyle: "italic" }}>{sessionLine}</div>}
            </div>
          </Fade>
        </div>
      );
    case "ticker":
      return (
        <div style={{ width: "100%", height: "100%", background: "#0b1120", color: "#fff", display: "flex", flexDirection: "column", justifyContent: "space-between", padding: "5vh 6vh" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: "3vh", fontWeight: 800, letterSpacing: 1 }}>{config.show_name}</div>
            <div style={{ fontSize: "1.8vh", padding: "0.8vh 2vh", background: "#dc2626", borderRadius: 999, letterSpacing: 3 }}>● AO VIVO</div>
          </div>
          <Fade>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "12vh", fontWeight: 900, letterSpacing: "-2px" }}>{text || event.full_name}</div>
              <div style={{ fontSize: "2.8vh", color: "#94a3b8", marginTop: "2vh", letterSpacing: 1 }}>bem-vindo(a) · {sessionLine}</div>
            </div>
          </Fade>
          <div style={{ background: "rgba(255,255,255,0.05)", borderRadius: 12, padding: "1.5vh 3vh", fontFamily: "monospace", fontSize: "2vh", color: "#94a3b8", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            ✓ {event.full_name}
          </div>
        </div>
      );
    case "neon":
      return (
        <div style={{ width: "100%", height: "100%", background: "#0a0a0a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", width: "50vw", height: "50vw", background: "radial-gradient(circle,rgba(236,72,153,0.3),transparent 70%)", top: "-15vw", left: "-15vw" }} />
          <div style={{ position: "absolute", width: "50vw", height: "50vw", background: "radial-gradient(circle,rgba(59,130,246,0.3),transparent 70%)", bottom: "-15vw", right: "-15vw" }} />
          <Fade>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "2.5vh", letterSpacing: 8, color: "#ec4899", textTransform: "uppercase", marginBottom: "3vh", textShadow: "0 0 20px #ec4899" }}>Bem-vindo(a)</div>
              <div style={{ fontSize: "12vh", fontWeight: 900, letterSpacing: "-2px", background: "linear-gradient(135deg,#ec4899,#3b82f6)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", textShadow: "0 0 60px rgba(236,72,153,0.5)" }}>{text || event.full_name}</div>
              {sessionLine && <div style={{ fontSize: "2.4vh", color: "#d4d4d8", marginTop: "3vh", letterSpacing: 4 }}>{sessionLine}</div>}
            </div>
          </Fade>
        </div>
      );
    case "typewriter":
      return (
        <div style={{ width: "100%", height: "100%", background: "#fafaf9", color: "#0a0a0a", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Courier New', monospace", padding: "0 10vh", position: "relative" }}>
          <Fade>
            <div>
              <div style={{ position: "absolute", top: "8vh", right: "10vh", transform: "rotate(12deg)", border: "3px solid #dc2626", color: "#dc2626", padding: "1vh 3vh", fontSize: "2vh", fontWeight: 700, letterSpacing: 4, opacity: 0.85 }}>CHECK-IN</div>
              <div style={{ fontSize: "2.5vh", marginBottom: "3vh", color: "#525b62" }}>&gt; Bem-vindo(a),</div>
              <div style={{ fontSize: "10vh", fontWeight: 700, letterSpacing: "-2px", lineHeight: 1.05, borderRight: "6px solid #0a0a0a", display: "inline-block", paddingRight: "1vh" }}>{text || event.full_name}</div>
              {sessionLine && <div style={{ fontSize: "2.5vh", color: "#71717a", marginTop: "3vh" }}>{sessionLine}</div>}
            </div>
          </Fade>
        </div>
      );
    case "minimalist":
    default:
      return (
        <div style={{ width: "100%", height: "100%", background: "#000", color: "#fff", display: "flex", alignItems: "center", justifyContent: "flex-start", padding: "0 10vh", position: "relative" }}>
          <Fade>
            <div>
              <div style={{ fontSize: "2.5vh", letterSpacing: 8, color: ac, textTransform: "uppercase", marginBottom: "3vh", fontWeight: 500 }}>Bem-vindo(a)</div>
              <div style={{ fontSize: "12vh", fontWeight: 800, letterSpacing: "-4px", lineHeight: 1, marginBottom: "3vh" }}>{text || event.full_name}</div>
              {sessionLine && <div style={{ fontSize: "2.4vh", color: "#71717a", letterSpacing: 4, textTransform: "uppercase" }}>{sessionLine}</div>}
            </div>
          </Fade>
          <div style={{ position: "absolute", bottom: "3vh", right: "4vh", fontSize: "1.6vh", letterSpacing: 4, color: "#3f3f46", textTransform: "uppercase" }}>{config.show_name}</div>
        </div>
      );
  }
}

/* =================== Helpers =================== */
function Fade({ children }) {
  return (
    <div style={{ animation: "fadeZoomIn 0.6s ease-out" }}>
      {children}
      <style>{`
        @keyframes fadeZoomIn {
          0% { opacity: 0; transform: scale(0.92); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes confetti-fall {
          0% { transform: translateY(-10vh) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0.6; }
        }
      `}</style>
    </div>
  );
}

function Confetti() {
  const colors = ["#fde047", "#34d399", "#f472b6", "#60a5fa", "#fb7185"];
  const pieces = Array.from({ length: 40 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 0.6,
    duration: 3 + Math.random() * 2,
    color: colors[i % colors.length],
    rotate: Math.random() * 360,
  }));
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {pieces.map(p => (
        <span key={p.id} style={{
          position: "absolute",
          top: "-5vh",
          left: `${p.left}%`,
          width: 8,
          height: 16,
          background: p.color,
          transform: `rotate(${p.rotate}deg)`,
          animation: `confetti-fall ${p.duration}s ease-in ${p.delay}s forwards`,
        }} />
      ))}
    </div>
  );
}
